import { createAction } from "redux-actions";
import { takeLatest, put, select, call, all } from "redux-saga/effects";
import axios from "axios";
import moment from "moment";
import { queryParams } from "../../../utils/queryParams";
import { API_DATE_FORMAT } from "../../../app_constants";

export const SET_LOADING = "./Demographics/SET_LOADING";
export const SET_ALL_SITES_MODE = "./Demographics/SET_ALL_SITES_MODE";
export const SET_DATES = "./Demographics/SET_DATES";
export const SET_FILTERS_DIRTY = "./Demographics/SET_FILTERS_DIRTY";
export const SET_UPDATES_LOADING = "./Demographics/SET_UPDATES_LOADING";
export const SET_LOADING_FILTERS = "./Demographics/SET_LOADING_FILTERS";

export const RESET_FILTER_SELECTED = "./Demographics/RESET_FILTER_SELECTED";
export const RESET_FILTER = "./Demographics/RESET_FILTERS";
export const GET_PIE_CHART_BY_NAME = "./Demographics/GET_PIE_CHART_BY_NAME";

export const FETCH_CURRENT_USER = "./Demographics/FETCH_CURRENT_USER";
export const FETCH_CURRENT_USER_SUCCESS = "./Demographics/FETCH_CURRENT_USER_SUCCESS";
export const FETCH_FILTERS_SUCCESS = "./Demographics/FETCH_FILTERS_SUCCESS";
export const FETCH_CHART_SUCCESS = "./Demographics/FETCH_CHART_SUCCESS";

export const UPDATE_DATES = "./Demographics/UPDATE_DATES";
export const UPDATE_CHART = "./Demographics/UPDATE_CHART";
export const UPDATE_SELECTED_FILTER = "./Demographics/UPDATE_SELECTED_FILTER";
export const UPDATE_REFERRAL_TOGGLE = "./Demographics/UPDATE_REFERRAL_TOGGLE";
export const UPDATE_FILTERS_SUCCESS = "./Demographics/UPDATE_FILTERS_SUCCESS";

export const actionCreators = {
  fetchCurrentUser: createAction(FETCH_CURRENT_USER),
  updateDates: createAction(UPDATE_DATES),
  updateData: createAction(UPDATE_CHART),
  updateReferralToggle: createAction(UPDATE_REFERRAL_TOGGLE),
  updateSelectedFilter: createAction(UPDATE_SELECTED_FILTER),
  resetFilters: createAction(RESET_FILTER)
};

export default function* watchActiveMembers() {
  yield takeLatest(FETCH_CURRENT_USER, fetchCurrentUserData);
  yield takeLatest(UPDATE_REFERRAL_TOGGLE, updateReferralToggle);
  yield takeLatest(UPDATE_CHART, updateData);
  yield takeLatest(RESET_FILTER, resetFilters);
}

function* fetchPieChart(url, action, name) {
  const { organization_id, short_url, startDate, endDate, referral_category_toggle } =
    yield getCommonApiParams();

  const params = {
    organization_id,
    short_url,
    end_date: endDate,
    start_date: startDate
  };

  const filterOptions = yield select((state) => state.demographics.selectedFilters);
  const filtersParams = Object.keys(filterOptions).map((paramKey) => {
    const value = filterOptions[paramKey];
    if (Array.isArray(value) && value.length > 0) {
      return {
        [paramKey]: value
          .map((val) => (paramKey === "selected_categories" ? val.id : val.name))
          .join(",")
      };
    } else if (typeof value === "object" && value.name) {
      return { [paramKey]: value.name };
    } else {
      return {
        [paramKey]: typeof value === "string" ? value.toLowerCase() : value
      };
    }
  });
  const filters = Object.assign({}, ...filtersParams);

  try {
    let { data } = yield call(axios.post, queryParams.apiVersion + `pages/demographics/${url}`, {
      ...params,
      filters: { ...filters, referral_category_toggle }
    });
    yield put({ type: action, payload: { data, name } });
  } catch (err) {
    console.error(err);
  }
}

function fetchAllCharts() {
  return [
    fetchPieChart("clients_by_gender", GET_PIE_CHART_BY_NAME, "chartByGender"),
    fetchPieChart("clients_by_age", GET_PIE_CHART_BY_NAME, "chartByAge"),
    fetchPieChart("clients_by_zip_code", GET_PIE_CHART_BY_NAME, "chartByZipCode"),
    fetchPieChart("clients_by_referral_category", GET_PIE_CHART_BY_NAME, "chartByReferralCategory"),
    getChart()
  ];
}

export function* fetchCurrentUserData(data) {
  yield put({
    type: FETCH_CURRENT_USER_SUCCESS,
    payload: data.payload
  });
  const { availableAllSitesView } = data.payload;
  yield put({ type: SET_LOADING, payload: true });
  let dates = yield getDatesHelper();
  yield setDates(...dates);

  if (!availableAllSitesView) {
    yield put({ type: SET_LOADING, payload: false });
    yield put({ type: SET_ALL_SITES_MODE, payload: true });
  } else {
    try {
      yield fetchFilters();
      yield all(fetchAllCharts());
      yield put({ type: SET_LOADING, payload: false });
    } catch (error) {
      console.error(error);
    }
  }
}

function* getChart() {
  const { organization_id, short_url, startDate, endDate, referral_category_toggle } =
    yield getCommonApiParams();

  const params = {
    organization_id,
    short_url,
    end_date: endDate,
    start_date: startDate
  };
  const filterOptions = yield select((state) => state.demographics.selectedFilters);

  const filtersParams = Object.keys(filterOptions).map((paramKey) => {
    const value = filterOptions[paramKey];
    if (Array.isArray(value) && value.length > 0) {
      return {
        [paramKey]: value
          .map((val) => (paramKey === "selected_categories" ? val.id : val.name))
          .join(",")
      };
    } else if (typeof value === "object" && value.name) {
      return { [paramKey]: value.name };
    } else {
      return {
        [paramKey]: typeof value === "string" ? value.toLowerCase() : value
      };
    }
  });

  const filters = Object.assign({}, ...filtersParams);

  try {
    let { data } = yield call(
      axios.post,
      queryParams.apiVersion + `pages/demographics/clients_table`,
      { ...params, filters: { ...filters, referral_category_toggle } }
    );
    yield put({
      type: FETCH_CHART_SUCCESS,
      payload: { tableData: { isLoading: false, content: data } }
    });
  } catch (err) {
    console.error(err);
  }
}

function* getCommonApiParams() {
  const { organization_id, short_url } = yield select((state) => state.commonReducer.commonProps);
  return yield select((state) => {
    const {
      selectedRequestDates: { startDate, endDate },
      referralCategory
    } = state.demographics;

    return {
      organization_id,
      short_url,
      startDate,
      endDate,
      referral_category_toggle: referralCategory
    };
  });
}

export function* fetchFilters(update) {
  const { organization_id, short_url, startDate, endDate, referral_category_toggle } =
    yield getCommonApiParams();

  const params = {
    organization_id,
    short_url,
    end_date: endDate,
    start_date: startDate
  };

  let url = queryParams.apiVersion + "pages/demographics/filters";
  try {
    let { data } = yield call(axios.post, url, {
      ...params,
      filters: { referral_category_toggle }
    });

    if (update) {
      yield put({ type: UPDATE_FILTERS_SUCCESS, payload: data });
    } else {
      yield put({ type: FETCH_FILTERS_SUCCESS, payload: data });
    }
  } catch (err) {
    console.error(err);
  }
}
export function* updateReferralToggle() {
  yield put({ type: SET_LOADING_FILTERS, payload: true });
  yield fetchFilters(true);
  yield put({ type: SET_LOADING_FILTERS, payload: false });
}

export function* setDates(startDate, endDate) {
  yield put({
    type: SET_DATES,
    payload: {
      startDate,
      endDate
    }
  });
}

function* updateData() {
  yield put({ type: SET_UPDATES_LOADING, payload: true });
  try {
    yield put({ type: SET_FILTERS_DIRTY, payload: false });
    yield all(fetchAllCharts());
    yield put({ type: SET_UPDATES_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* resetFilters() {
  yield put({ type: SET_UPDATES_LOADING, payload: true });
  let dates = yield getDatesHelper();
  yield setDates(...dates);
  yield put({ type: RESET_FILTER_SELECTED });
  yield fetchFilters(true);
  try {
    yield all(fetchAllCharts());
    yield put({ type: SET_UPDATES_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* getDatesHelper() {
  const endDate = yield select((state) => state.commonReducer.commonProps.calendarEndDate);
  const startDate = moment(endDate).startOf("month").subtract(11, "months").format(API_DATE_FORMAT);
  return [startDate, endDate];
}
